import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { isValidUrl } from '@bingads-webui/url-util';
import { CroppedImageView } from '@bingads-webui-campaign-react/cropped-image-view';

import {
  MMAPlacementCard,
  AdLabel,
  PlacementGradient,
  PlacementHeadline,
  PlacementFinalUrl,
  PlacementDescription,
  PlacementAction,
  BusinessNameWithAdsLabel,
} from '../shared-components';
import dotsIcon from './media/dots.svg';
import type { UAPPlacementBaseProps } from '../@types';

export const AdPlacementMmaBase = ({
  // UAPVMSingleImagePlacement
  placementData,

  isEnabled,
  isContentHubMicrosoftPlus,
  showShortHeadline,
  shortHeadline,
  isMultiLineShortHeadline,
  showLongHeadline,
  longHeadline,
  isMultiLineLongHeadline,
  showFinalUrl,
  finalUrl,
  isMultiLineFinalUrl,
  displayUrl,
  displayFinalUrlOrigin,
  showDescription,
  description,
  isMultiLineDescription,
  showCallToAction,
  callToAction,
  showAdLabel,
  showGradient,
  showBusinessName,
  businessName,
  showBusinessNameAdLabel,
  showAdsIcon,
  showLogo,
  logo,
  showDots,
  previewAriaLabel,

  // may need to set window._TL_ in other platforms
  i18n,

  classes,
}: UAPPlacementBaseProps) => {
  const {
    placementMeta, placementCropping, imageMeta, effectiveImageUrl, effectiveImageWidth, effectiveImageHeight, expandedPlacementCropping,
  } = placementData || {};
  const {
    previewHeight, aspectRatio, logoHeight, imageBorderRadius,
  } = placementMeta || {};

  // const srcForCroppedImageView = showImage && this.props.image.FinalSrc;
  const imagePreviewWidth = previewHeight * aspectRatio;

  let finalUrlOrigin;
  if (displayFinalUrlOrigin && isValidUrl(finalUrl)) {
    finalUrlOrigin = new URL(finalUrl).origin;
  }

  const longHeadlineText = isContentHubMicrosoftPlus ? [longHeadline, description].join(' ') : longHeadline;

  if (!placementData || !imageMeta) {
    return null;
  }

  const cropping = expandedPlacementCropping || placementCropping;

  return (
    <MMAPlacementCard
      url={finalUrl}
      width={imagePreviewWidth}
      isEnabled={isEnabled}
      mmaPlacementCardClassName={classes.placementCard}
      ariaLabel={previewAriaLabel}
    >
      <div className={classnames(classes.placementImageContainer)}>
        {showAdLabel && <AdLabel i18n={i18n} adsLabelClassName={classes.placementAdsLabel} />}
        {showGradient && <PlacementGradient gradientClassName={classes.placementGradient} />}
        {
          imageMeta.url &&
          <CroppedImageView
            resizeImageHeight={previewHeight}
            aspectRatio={aspectRatio}
            src={effectiveImageUrl || imageMeta.url}
            originalWidth={effectiveImageWidth || imageMeta.originalWidth}
            originalHeight={effectiveImageHeight || imageMeta.originalHeight}
            SourceX={cropping && cropping.SourceX}
            SourceY={cropping && cropping.SourceY}
            SourceWidth={cropping && cropping.SourceWidth}
            SourceHeight={cropping && cropping.SourceHeight}
            borderRadius={imageBorderRadius}
          />
        }
      </div>
      <div className={classnames(classes.placementTextContainer)}>
        {showLogo && logo &&
          <div className="logo-and-text-container">
            <div className={classnames(classes.placementLogoContainer, 'logo-container')}>
              <CroppedImageView
                resizeImageHeight={logoHeight || 24}
                src={logo.Src}
                originalWidth={logo.Width}
                originalHeight={logo.Height}
                SourceX={logo.CropSettings.SourceX}
                SourceY={logo.CropSettings.SourceY}
                SourceWidth={logo.CropSettings.SourceWidth}
                SourceHeight={logo.CropSettings.SourceHeight}
                aspectRatio={1}
              />
            </div>
            <div className="logo-text">
              <div className={classnames(classes.placementBusinessNameContainer, 'logo-business-name')}>{businessName}</div>
              {showFinalUrl &&
                <PlacementFinalUrl

                  finalUrl={finalUrl}
                  displayUrl={displayUrl || finalUrlOrigin}
                  isMultiLine={isMultiLineFinalUrl}
                  placementFinalUrlClassName={classnames(classes.placementFinalUrl, 'final-url')}
                />
              }
            </div>
          </div>
        }
        {showDots && (
          <img className={classes.placementDotsIcon} alt="" src={dotsIcon} />
        )}
        {showShortHeadline && (
          <PlacementHeadline

            headlineText={shortHeadline}
            isMultiLine={isMultiLineShortHeadline}
            placementHeadlineClassName={classnames(classes.placementShortHeadline, 'short-headline')}
          />
        )}
        {showLongHeadline && (
          <PlacementHeadline

            headlineText={longHeadlineText}
            isMultiLine={isMultiLineLongHeadline}
            placementHeadlineClassName={classnames(classes.placementLongHeadline, 'long-headline')}
          />
        )}
        {(!showLogo || !logo) && showFinalUrl && (
          <PlacementFinalUrl

            finalUrl={finalUrl}
            displayUrl={displayUrl || finalUrlOrigin}
            isMultiLine={isMultiLineFinalUrl}
            placementFinalUrlClassName={classnames(classes.placementFinalUrl, 'final-url')}
          />
        )}
        {showDescription && (
          <PlacementDescription

            description={description}
            isMultiLine={isMultiLineDescription}
            placementDescriptionClassName={classnames(classes.placementDescription, 'description')}
          />
        )}
        {showBusinessName && (
          <BusinessNameWithAdsLabel
            showAdsLabel={showBusinessNameAdLabel}
            businessName={businessName}
            showAdsIcon={showAdsIcon}
            i18n={i18n}
            rootClassName={classnames(classes.placementBusinessNameWithAdsLabel, 'business-name-with-ads')}
          />
        )}
        {showCallToAction && callToAction && ( // add a space before each capital letter, e.g. "ActNow" => "Act Now"
          <PlacementAction actionText={callToAction.replace(/([A-Z])/g, ' $1')} placementActionClassName={classes.placementCallToAction} />
        )}
      </div>
    </MMAPlacementCard>
  );
};

AdPlacementMmaBase.propTypes = {
  placementData: PropTypes.shape({}).isRequired, // UAPVMSingleImagePlacement
  isEnabled: PropTypes.bool,
  isContentHubMicrosoftPlus: PropTypes.bool,

  showShortHeadline: PropTypes.bool,
  shortHeadline: PropTypes.string,
  isMultiLineShortHeadline: PropTypes.bool,

  showLongHeadline: PropTypes.bool,
  longHeadline: PropTypes.string,
  isMultiLineLongHeadline: PropTypes.bool,

  showFinalUrl: PropTypes.bool,
  finalUrl: PropTypes.string,
  isMultiLineFinalUrl: PropTypes.bool,
  displayFinalUrlOrigin: PropTypes.bool,
  displayUrl: PropTypes.string,

  showDescription: PropTypes.bool,
  description: PropTypes.string,
  isMultiLineDescription: PropTypes.bool,

  showCallToAction: PropTypes.bool,
  callToAction: PropTypes.string,

  showAdLabel: PropTypes.bool,
  showGradient: PropTypes.bool,

  showBusinessName: PropTypes.bool,
  businessName: PropTypes.string,
  showBusinessNameAdLabel: PropTypes.bool,
  showAdsIcon: PropTypes.bool,

  showLogo: PropTypes.bool,
  logo: PropTypes.shape({
    Src: PropTypes.string,
    Width: PropTypes.number,
    Height: PropTypes.number,
    CropSettings: PropTypes.shape({
      SourceX: PropTypes.number,
      SourceY: PropTypes.number,
      SourceWidth: PropTypes.number,
      SourceHeight: PropTypes.number,
    }),
  }),

  showDots: PropTypes.bool,
  previewAriaLabel: PropTypes.string,

  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  classes: PropTypes.shape({
    placementCard: PropTypes.string,
    placementImageContainer: PropTypes.string,
    placementLogoContainer: PropTypes.string,
    placementBusinessNameContainer: PropTypes.string,
    placementTextContainer: PropTypes.string,
    placementAdsLabel: PropTypes.string,
    placementGradient: PropTypes.string,
    placementShortHeadline: PropTypes.string,
    placementLongHeadline: PropTypes.string,
    placementFinalUrl: PropTypes.string,
    placementDescription: PropTypes.string,
    placementCallToAction: PropTypes.string,
    placementBusinessNameWithAdsLabel: PropTypes.string,
    placementDotsIcon: PropTypes.string,
  }).isRequired,
};

AdPlacementMmaBase.defaultProps = {
  isEnabled: true,
  isContentHubMicrosoftPlus: false,

  showShortHeadline: false,
  showLongHeadline: false,
  showFinalUrl: false,
  displayFinalUrlOrigin: false,
  showDescription: false,
  showCallToAction: false,
  showAdLabel: false,
  showGradient: false,
  showBusinessName: false,
  showBusinessNameAdLabel: false,
  showLogo: false,
  showAdsIcon: false,

  shortHeadline: undefined,
  isMultiLineShortHeadline: false,
  longHeadline: undefined,
  isMultiLineLongHeadline: false,
  finalUrl: undefined,
  displayUrl: undefined,
  isMultiLineFinalUrl: false,
  description: undefined,
  isMultiLineDescription: false,
  callToAction: undefined,
  businessName: undefined,
  logo: undefined,
  showDots: false,
  previewAriaLabel: undefined,
};
